import { useCallback } from 'react';
import { API_CORE } from '../../../../../_CORE/API/ApiCORE';
import { confirm } from '../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost';
import { notify } from '../../../../../_CORE/APP/Templates/_UI/Alerts/AppAlertHost';
import { SECURITY_ADMIN_CONSTANTS as S } from '../_MODULES/SecurityApi';

const normalizeSecurity = (response, fallback) => {
    const status = response?.status ?? response?.code_status ?? 500;
    if (status >= 400 || response?.success === false) {
        throw new Error(response?.message || fallback);
    }
    return response?.data ?? response;
};

const cancelledError = () => {
    const error = new Error('Operacion cancelada.');
    error.cancelled = true;
    return error;
};

export const attachSecurityToken = (data, actionToken) => {
    if (!actionToken) return data;

    if (typeof FormData !== 'undefined' && data instanceof FormData) {
        data.set('_security_action_token', actionToken);
        return data;
    }

    if (data && typeof data === 'object' && !Array.isArray(data)) {
        return { ...data, _security_action_token: actionToken };
    }

    return { value: data, _security_action_token: actionToken };
};

export const useActionVerification = () => {
    const authorize = useCallback(async (actionKey) => {
        const REQUEST_ENDPOINT = API_CORE.ENDPOINTS[S.ACTION_REQUEST];
        const VERIFY_ENDPOINT = API_CORE.ENDPOINTS[S.ACTION_VERIFY];
        if (!REQUEST_ENDPOINT || !VERIFY_ENDPOINT) throw new Error('Endpoints de seguridad no registrados.');

        try {
            const requestResponse = await API_CORE.SERVICES.POST_RESOURCE(REQUEST_ENDPOINT, { action_key: actionKey });
            const challenge = normalizeSecurity(requestResponse, 'No se pudo solicitar el codigo de seguridad.');

            if (challenge?.required === false) {
                throw new Error('La verificacion por codigo es obligatoria y el servidor intento omitirla.');
            }

            notify.info(
                'Codigo de seguridad enviado',
                `Revisa ${challenge?.emailMasked || 'tu correo'} antes de continuar.`
            );

            while (true) {
                const code = await confirm.code({
                    title: 'Autorizar esta accion',
                    emailMasked: challenge?.emailMasked || '',
                    expiresIn: challenge?.expiresIn || '',
                    text: `Para continuar con esta modificacion, ingresa el codigo de 6 digitos enviado a ${challenge?.emailMasked || 'tu correo'}.`,
                });

                if (!code) throw cancelledError();

                try {
                    const verifyResponse = await API_CORE.SERVICES.POST_RESOURCE(VERIFY_ENDPOINT, {
                        action_key: actionKey,
                        challenge_token: challenge?.challengeToken,
                        code: String(code).trim(),
                    });
                    const verified = normalizeSecurity(verifyResponse, 'No se pudo verificar el codigo de seguridad.');
                    if (!verified?.actionToken) throw new Error('El servidor no devolvio la autorizacion de seguridad.');
                    return verified.actionToken;
                } catch (verifyError) {
                    notify.error('Codigo no valido', verifyError?.message || 'Revisa el codigo e intenta nuevamente.');
                    verifyError.notified = true;
                    const terminal = /vencio|agotaron|ya no es valido/i.test(String(verifyError?.message || ''));
                    if (terminal) throw verifyError;
                }
            }
        } catch (error) {
            if (!error?.cancelled && !error?.notified) {
                notify.error('Verificacion requerida', error?.message || 'No se pudo verificar la accion.');
                error.notified = true;
            }
            throw error;
        }
    }, []);

    return { authorize };
};
